import { AddIcon, Button, EmptyState, NoTasksIcon } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getTypeText } from 'utils/eventHelper';
import { naturalStringSort } from 'utils/stringHelper';
import { filterEventsByHolderSettings } from '../../../utils/filterEventsByHolderSettings';
import { BpPermissionChecker } from '../../../utils/PermissionChecker';
import { TodayEvent, TodayEventType } from './TodayEvent';
import styles from './TodayEventList.module.scss';

const TodayEventListFragment = graphql(`
  fragment TodayEventListFields on Query {
    events: calendarEvents(where: $eventWhere) {
      __typename
      uuid
      title
      description
      start
      duration
      categories
      prodId
      virtualLocations {
        __typename
        uuid
        token
        running
      }
      holderConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            ... on Group {
              __typename
              uuid
              showImportedLessonEvents
              organization {
                uuid
              }
            }
            ... on Profile {
              __typename
              uuid
              organization {
                uuid
              }
            }
            ... on Organization {
              __typename
              uuid
            }
          }
        }
      }
    }
  }
`);

type TodayEventListProps = {
  showAddButton?: boolean;
  onAdd?: () => void;
  refetchData?: () => void;
  query: FragmentType<typeof TodayEventListFragment> | undefined;
  permissionChecker: BpPermissionChecker | null;
};

export const TodayEventList = ({
  showAddButton,
  onAdd,
  refetchData,
  query,
  permissionChecker,
}: TodayEventListProps) => {
  const { t } = useTranslation();
  const today = dayjs();

  const fragment = getFragmentData(TodayEventListFragment, query);
  const filtered = fragment ? filterEventsByHolderSettings(fragment.events) : [];

  const events: TodayEventType[] =
    filtered
      ?.map((e) => {
        const start = dayjs(e.start);
        const duration = isNaN(Number(e.duration)) ? 5400 : Number(e.duration);
        const end = start.add(duration, 'seconds');
        const beforeToday = start.isBefore(today, 'days');
        const afterToday = end.isAfter(today, 'days');

        // attendees allowed to start/end the conference!
        const canStartStopMeeting = e.holderConnection.edges.some(
          (g) =>
            g.node.__typename === 'Group' &&
            permissionChecker?.isGroupEditor(g.node) &&
            permissionChecker?.isOrganizationMember(g.node.organization),
        );

        return {
          uuid: e.uuid,
          name: e.title ?? t('events.title', { count: 1 }),
          description: e.description ?? null,
          from: start.toDate(),
          to: end.toDate(),
          timeFrameLabel:
            beforeToday && afterToday
              ? t('common.fullDay')
              : beforeToday
                ? `${start.format('DD.MM.YY, HH:mm')} – ${t('common.today')}, ${end.format('HH:mm')}`
                : afterToday
                  ? `${start.format('HH:mm')} – ${end.format('DD.MM.YY, HH:mm')}`
                  : `${start.format('HH:mm')} – ${end.format('HH:mm')}`,
          virtual: !(e.categories?.length === 1 && e.categories?.includes('onSite')),
          running: !!e.virtualLocations.find((v) => v.running),
          canStartStopMeeting,
          eventLink:
            e.virtualLocations && e.virtualLocations.length > 0
              ? import.meta.env.VITE_APP_REDIRECT_URI + '/join/' + e.virtualLocations[0].token
              : null,
          token: e.virtualLocations && e.virtualLocations.length > 0 ? e.virtualLocations[0].token : null,
          type: getTypeText(e.categories ?? [], 'short'),
        };
      })
      .sort((a, b) => {
        const aStart = dayjs(a.from);
        const aEnd = dayjs(a.to);
        const bStart = dayjs(b.from);
        const bEnd = dayjs(b.to);

        // Group 1: start < today && end > today (ganztägig)
        const aGroup1 = aStart.isBefore(today, 'day') && aEnd.isAfter(today, 'day');
        const bGroup1 = bStart.isBefore(today, 'day') && bEnd.isAfter(today, 'day');

        if (aGroup1 && !bGroup1) return -1;
        if (!aGroup1 && bGroup1) return 1;
        if (aGroup1 && bGroup1) return naturalStringSort(a.name, b.name);

        // Group 2: start < today && end is today
        const aGroup2 = aStart.isBefore(today, 'day') && aEnd.isSame(today, 'day');
        const bGroup2 = bStart.isBefore(today, 'day') && bEnd.isSame(today, 'day');

        if (aGroup2 && !bGroup2) return -1;
        if (!aGroup2 && bGroup2) return 1;

        // Group 3: start is today && end is today or after today
        const aGroup3 = aStart.isSame(today, 'day') && (aEnd.isSame(today, 'day') || aEnd.isAfter(today, 'day'));
        const bGroup3 = bStart.isSame(today, 'day') && (bEnd.isSame(today, 'day') || bEnd.isAfter(today, 'day'));

        if (aGroup3 && !bGroup3) return -1;
        if (!aGroup3 && bGroup3) return 1;

        // Fallback: sort by start date
        return aStart.valueOf() - bStart.valueOf();
      }) ?? [];

  return (
    <div className={styles['today-event-list']}>
      <div className={styles.header}>
        <div className={styles.headline}>{t('events.todayEvents')}</div>
        {showAddButton && <Button icon={<AddIcon />} hierarchy='ghost' onClick={onAdd} />}
      </div>
      <div className={styles.events}>
        {events.length === 0 ? (
          <EmptyState icon={<NoTasksIcon />} title={t('events.noEvents')} className='mt-6 mb-10' />
        ) : (
          events.map((e) => <TodayEvent key={e.uuid} event={e} refetchData={refetchData} />)
        )}
      </div>
    </div>
  );
};
